class DeliveryCutoff extends HTMLElement {
  constructor() {
    super();
    this.intervalId = null;
    this.lockDaysFilter = null;
  }

  connectedCallback() {
    this.dayOfWeek = [
      this.dataset.sundayLabel || "Sunday",
      this.dataset.mondayLabel || "Monday",
      this.dataset.tuesdayLabel || "Tuesday",
      this.dataset.wednesdayLabel || "Wednesday",
      this.dataset.thursdayLabel || "Thursday",
      this.dataset.fridayLabel || "Friday",
      this.dataset.saturdayLabel || "Saturday",
    ];
    this.sunday = this.dayOfWeek[0];
    this.monday = this.dayOfWeek[1];
    const cfg = window.QF && window.QF.deliveryDatePicker ? window.QF.deliveryDatePicker : {};
    this.nonDeliveryDates = [...new Set(
      (this.dataset.nonDeliveryDates || "")
        .split(",")
        .map(d => parseInt(d.trim(), 10))
        .filter(n => !isNaN(n) && n >= 0 && n <= 6)
    )];
    const unavailableWeekdays =
      cfg.unavailableWeekdays !== undefined ? cfg.unavailableWeekdays : this.dataset.nonDeliveryDates || "";
    const effectiveWeekdays = this.withSubscriptionSaturday(unavailableWeekdays);
    const holidays = cfg.bankHolidays !== undefined ? cfg.bankHolidays : "";
    this.lockDaysFilter = this.buildLockDaysFilter(effectiveWeekdays, holidays);

    this.updateTimer();
    this.intervalId = setInterval(() => this.updateTimer(), 60000);
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  isNonDeliveryDate(dayIndex) {
    return this.nonDeliveryDates.includes(dayIndex);
  }

  addDays(d, n = 0) {
    const x = new Date(d);
    x.setDate(x.getDate() + Number(n));
    return x;
  }

  getDebugNowOrNow() {
    const now = new Date();
    const params = new URLSearchParams(window.location.search);
    const debugDateParam = params.get("debugDate") || params.get("debug_date");

    if (!debugDateParam) return now;

    const localDateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(debugDateParam);
    if (localDateOnlyMatch) {
      const [, y, m, d] = localDateOnlyMatch;
      const parsedLocal = new Date(Number(y), Number(m) - 1, Number(d));
      if (!isNaN(+parsedLocal)) return parsedLocal;
    }

    const parsed = new Date(debugDateParam);
    if (isNaN(+parsed)) return now;
    return parsed;
  }

  parseWeekdaySet(value) {
    return new Set(
      String(value || "")
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n >= 0 && n <= 6),
    );
  }

  withSubscriptionSaturday(weekdaysValue) {
    const weekdays = this.parseWeekdaySet(weekdaysValue);
    if (this.cartHasSubscription()) {
      weekdays.add(6);
    }
    return Array.from(weekdays).join(",");
  }

  cartHasSubscription() {
    const lineItems = document.querySelectorAll(
      "[data-cart-line-item], [data-cart-line], [cart-items-container] .item",
    );
    if (!lineItems.length) return false;

    return Array.from(lineItems).some((lineItem) => {
      const rawValue = lineItem.getAttribute("data-subscription");
      if (rawValue == null) return false;
      return String(rawValue).trim().toLowerCase() === "true";
    });
  }

  buildLockDaysFilter(weekdaysArray, holidaysArray) {
    const weekdays = this.parseWeekdaySet(weekdaysArray);

    const toKey = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    const re = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const holidays = new Set(
      String(holidaysArray || "")
        .split(",")
        .map((s) => s.trim())
        .map((s) => {
          const m = re.exec(s);
          if (!m) return null;
          const [, dd, mm, yyyy] = m;
          const dt = new Date(+yyyy, +mm - 1, +dd);
          return isNaN(+dt) ? null : toKey(dt);
        })
        .filter(Boolean),
    );

    return (dateLike) => {
      const d =
        dateLike && dateLike.dateInstance instanceof Date ? dateLike.dateInstance : dateLike;
      if (!(d instanceof Date) || isNaN(+d)) return false;
      return weekdays.has(d.getDay()) || holidays.has(toKey(d));
    };
  }

  getCutoffs() {
    const cutoffs = [
      this.dataset.sundayCutoff || "-1",
      this.dataset.mondayCutoff || "-1",
      this.dataset.tuesdayCutoff || "-1",
      this.dataset.wednesdayCutoff || "-1",
      this.dataset.thursdayCutoff || "-1",
      this.dataset.fridayCutoff || "-1",
      this.dataset.saturdayCutoff || "-1",
    ];

    return cutoffs.map((c) => {
      const val = parseInt(c, 10);
      return Number.isFinite(val) && val >= 0 ? val : -1;
    });
  }

  getNow() {
    return this.getDebugNowOrNow();
  } 

  updateTimer() {
    const DAY_IN_MS = 24 * 60 * 60 * 1000;
    const cutOffs = this.getCutoffs();
    const date = this.getNow();
    const endDate = this.getNow();

    const currentTime =
      String(date.getHours()).padStart(2, "0") + String(date.getMinutes()).padStart(2, "0");
    const day = date.getDay();

    let delivering =
      this.dayOfWeek[(day + 1) % 7] === this.sunday ? this.monday : this.dayOfWeek[(day + 1) % 7];

    let foundWindow = false;

    // Search forward for the first dispatch window that leads to an allowed delivery day.
    for (let i = 0; i < 35; i++) {

      const testDay = day + i;
      const cutOff = cutOffs[testDay % 7];
      const deliveringDay = day + i + 1;
      if (cutOff === -1) continue;
      if (i === 0 && parseInt(currentTime, 10) >= cutOff) continue;

      const deliveryDate = this.addDays(date, i + 1);
      deliveryDate.setHours(0, 0, 0, 0);

      if (this.lockDaysFilter && this.lockDaysFilter(deliveryDate)) continue;
      if (!this.lockDaysFilter && this.isNonDeliveryDate(deliveryDate.getDay())) continue;

      delivering =
        this.dayOfWeek[deliveringDay % 7] === this.sunday
          ? this.monday
          : this.dayOfWeek[deliveringDay % 7];

      endDate.setDate(date.getDate() + i);
      const hoursPart = Math.floor(cutOff / 100);
      const minutesPart = cutOff % 100;
      endDate.setHours(hoursPart, minutesPart, 0, 0);
      foundWindow = true;
      break;
    }

    const timerCopy = this.dataset.timerCopy || "";
    const cutoffCopy = this.dataset.cutoffCopy || "";
    const timeUntilCutoff = endDate.getTime() - date.getTime();

    if (!foundWindow || timeUntilCutoff <= 0 || timeUntilCutoff > DAY_IN_MS) {
      this.innerHTML = cutoffCopy;
      return;
    }

    let difference = timeUntilCutoff;
    const hours = Math.floor(difference / (60.0 * 60.0 * 1000.0));
    difference = difference - hours * (60.0 * 60.0 * 1000.0);
    const minutes = Math.floor(difference / (60.0 * 1000.0));
    difference = difference - minutes * (60.0 * 1000.0);
    const seconds = Math.floor(difference / 1000.0);

    let outText = timerCopy;

    outText = outText
      .replaceAll("[hours]", hours)
      .replaceAll("[minutes]", minutes)
      .replaceAll("[seconds]", seconds)
      .replaceAll("[nextday]", delivering);

    this.innerHTML = outText;
  }
}

customElements.define("delivery-cutoff", DeliveryCutoff);
